import React, { useMemo } from "react";
import {
    StandardModalHeader,
    StandardModalFooter,
    PlainModal,
} from "@design-system/Modal/Modal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useDeleteAccountsBulkMutation } from "@api/account.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { pluralizeWithCount } from "@common/helpers/string";
import { businessReachClause, useBusinessNamesFor } from "./reachCopy";

interface AccountRowConstituent {
    id: string;
    name: string;
    entityId: number;
}

interface DeleteChartOfAccountsModalProps {
    show?: boolean;
    onHide?: () => void;
    accounts: AccountRowConstituent[];
    // Editor override: when set, confirm runs this instead of the chart page's
    // `deleteAccountsBulk` mutation. The workspace editor keeps its own delete
    // confirmation, so this is unused there today, but the shape stays uniform.
    onConfirm?: () => void;
}

export const DeleteChartOfAccountsModal: React.FC<
    DeleteChartOfAccountsModalProps
> = ({ show, onHide, accounts, onConfirm }) => {
    const deleteAccountsBulk = useDeleteAccountsBulkMutation();
    const { toast, error: toastError } = useToaster();
    const businessNamesFor = useBusinessNamesFor();

    const uniqueEntityIds = useMemo(
        () => Array.from(new Set(accounts.map((account) => account.entityId))),
        [accounts],
    );

    const accountName = accounts[0]?.name ?? "";
    const reach = businessReachClause(businessNamesFor(uniqueEntityIds));

    const handleConfirm = async () => {
        if (onConfirm) {
            onConfirm();
            onHide?.();
            return;
        }
        const body = accounts.map((account) => ({
            accountId: account.id,
            entityId: account.entityId,
        }));
        try {
            const results = await deleteAccountsBulk.mutateAsync(body);
            const successCount = results.filter((r) => r.success).length;
            const totalCount = results.length;
            if (successCount === totalCount) {
                toast(`Deleted ${pluralizeWithCount("account", successCount)}`);
            } else {
                const failedCount = totalCount - successCount;
                toast(
                    `Deleted ${pluralizeWithCount("account", successCount)}, ${failedCount} failed`,
                );
            }
            onHide?.();
        } catch {
            toastError("Failed to delete accounts");
        }
    };

    return (
        <PlainModal show={show} onHide={onHide} size="md">
            <StandardModalHeader>
                Are you sure you want to delete this account?
            </StandardModalHeader>
            <p>
                Because the account doesn't contain any journal entries, it will
                be permanently deleted. This action cannot be undone.
            </p>
            <p>{`This will delete ${accountName} in ${reach}.`}</p>
            <StandardModalFooter className="mt-4">
                <Button
                    type="button"
                    variant="secondary"
                    onClick={onHide}
                    size="xl"
                    disabled={deleteAccountsBulk.isPending}
                >
                    Cancel
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    type="button"
                    loading={deleteAccountsBulk.isPending}
                    size="xl"
                    onClick={handleConfirm}
                >
                    Delete
                </ButtonWithLoader>
            </StandardModalFooter>
        </PlainModal>
    );
};
